import { ProductScope, UserRole } from '@interfaces/auth';
import { subconjuntosDoPainel } from '@utils/subconjuntosDoPainel';

export type FamiliaDoPainel = {
  /** `afiliado` monta os blocos da fatia de quem divulga; `produtor`, os que falam pela comunidade. */
  familia: 'produtor' | 'afiliado';
  /** As comunidades que ESSA família serve, dentro da seleção. */
  botConfigIds: number[];
};

/**
 * O que cada família diz sobre si, quando há mais de uma na tela.
 *
 * Os rótulos falam do que o NÚMERO significa, e não do papel de quem olha: a família de produtor
 * inclui a comunidade alcançada por grant com escopo de produto, onde a pessoa não é criadora nem
 * coprodutora — chamá-la de "como criador(a)" mentiria para justamente quem a #360 existe para
 * servir.
 */
export const ROTULO_DA_FAMILIA: Record<
  FamiliaDoPainel['familia'],
  { titulo: string; explicacao: string }
> = {
  produtor: {
    titulo: 'Os números das comunidades',
    explicacao:
      'Faturamento, funil e base das comunidades em que você responde pelo produto ou tem acesso aos números.',
  },
  afiliado: {
    titulo: 'As suas vendas como afiliado(a)',
    explicacao: 'A sua comissão nas comunidades em que você divulga.',
  },
};

type Args = {
  /** O papel que a pessoa escolheu no filtro, se escolheu. */
  papelEscolhido?: UserRole;
  botConfigIds: number[];
  /** O escopo resolvido de cada produto (api#277). Vazio = `platform-api` anterior. */
  escopos: ProductScope[];
};

/**
 * Quais famílias de blocos o painel monta, e sobre quais comunidades cada uma.
 *
 * **O papel escolhido decide, e o descritor decide só quando não há escolha. A ordem é o assunto
 * desta função, e inverter foi um defeito real.**
 *
 * Quem é afiliada numa comunidade e tem grant de produto inteiro nela recebe `PRODUCT` no descritor
 * — o grant é absoluto onde existe (§5). Deduzir a família dali renderizaria os blocos de PRODUTOR;
 * só que os pedidos continuam mandando o papel escolhido como estreitamento, que o servidor aceita
 * porque a afiliação existe de verdade. Os números voltariam recortados por afiliação debaixo de
 * rótulos de produtor, e o funil — que omite o papel — em escopo de produto. Dois escopos na mesma
 * tela, e nada dizendo qual é qual.
 *
 * O papel escolhido é o que a pessoa PERGUNTOU, e respeitá-lo primeiro é a mesma precedência do
 * servidor: o estreitamento pedido vence onde o vínculo existe.
 *
 * Com papel escolhido, a seleção vai inteira — a lista já vem recortada por aquele papel. É o
 * comportamento de hoje, byte a byte.
 *
 * Sem papel escolhido, quem responde é o descritor: é o modo que a #360 introduz quando a lista
 * deixar de ser recortada, e o único em que a seleção pode ser mista — e aí a resposta tem DUAS
 * entradas, uma por família, cada uma com as suas comunidades.
 */
export const familiasDoPainel = ({
  papelEscolhido,
  botConfigIds,
  escopos,
}: Args): FamiliaDoPainel[] => {
  if (papelEscolhido)
    return [
      {
        familia: papelEscolhido === 'AFFILIATED' ? 'afiliado' : 'produtor',
        botConfigIds,
      },
    ];

  const subconjuntos = subconjuntosDoPainel(botConfigIds, escopos);

  /*
    Uma entrada por família COM comunidade, e as duas juntas quando a seleção é mista.
    Família vazia não entra — e não entrar é o que impede a requisição: o corpo dela não é montado,
    então nenhum hook dela roda. Era o impedimento que o código antigo registrava ("hook não pode ser
    condicional, então esconder a saída deixaria a requisição sair"), e ele se resolve movendo a
    condição para MONTAR, em vez de para exibir.

    Produtor primeiro porque é a família que fala pela comunidade — inclusive a alcançada por grant,
    que é quem a #360 existe para servir. A ordem é a da leitura, não uma precedência: agora as duas
    aparecem.
  */
  return [
    { familia: 'produtor' as const, botConfigIds: subconjuntos.produtor },
    { familia: 'afiliado' as const, botConfigIds: subconjuntos.afiliado },
  ].filter((entrada) => entrada.botConfigIds.length > 0);
};
